import { useCallback, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { usePaymentCore } from "@/features/payment";
import { usePaymentCoreStore } from "@/features/payment/store/payment-core-store";
import { useUserRefresh } from "@/hooks";

export const usePaymentNavigation = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { closePaymentModal, setCurrentStep } = usePaymentCore();
  const { openPaymentModal } = usePaymentCoreStore();
  const { refreshUser } = useUserRefresh();

  // URL'den payment durumunu kontrol et
  useEffect(() => {
    const pathname = location.pathname;

    // Stripe success/cancel URL'lerini kontrol et
    if (pathname === "/subscription-success") {
      // session_id NAVIGATE'DEN ÖNCE okunmalı. React iki state güncellemesini
      // birlikte işlediği için PaymentSuccessPage mount olduğunda URL çoktan
      // "/?payment=success" olmuş oluyor; parametre store'a taşınmazsa ödeme
      // doğrulaması hiç çalışmıyor.
      const sessionId =
        new URLSearchParams(location.search).get("session_id") ?? undefined;
      // Modal'ı success step'i ile aç
      openPaymentModal("stripe", sessionId, undefined, "success");
      // URL'yi temizle
      navigate("/?payment=success", { replace: true });
    } else if (pathname === "/subscription-cancel") {
      // Modal'ı cancel step'i ile aç
      openPaymentModal("stripe", undefined, undefined, "cancel");
      // URL'yi temizle
      navigate("/?payment=fail", { replace: true });
    }
  }, [location.pathname, location.search, navigate, openPaymentModal]);

  // Navigate to home page
  const navigateToHome = useCallback(() => {
    // Close payment modal and navigate to home
    closePaymentModal();
    navigate("/", { replace: true });
  }, [closePaymentModal, navigate]);

  // Navigate to payment page
  const navigateToPayment = useCallback(
    (provider: string, sessionId?: string, sessionUrl?: string) => {
      openPaymentModal(provider, sessionId, sessionUrl);
    },
    [openPaymentModal]
  );

  // Handle payment success navigation
  const handlePaymentSuccess = useCallback(async () => {
    setCurrentStep("success");

    // Refresh user data after successful payment
    try {
      await refreshUser({ silent: true });
    } catch {
      console.warn("⚠️ Failed to refresh user data after payment success");
    }
  }, [setCurrentStep, refreshUser]);

  // Handle payment cancel navigation
  const handlePaymentCancel = useCallback(() => {
    setCurrentStep("cancel");
    navigateToHome();
  }, [setCurrentStep, navigateToHome]);

  return {
    navigateToHome,
    navigateToPayment,
    handlePaymentSuccess,
    handlePaymentCancel,
    currentPath: location.pathname,
  };
};
